import { parseSubject } from '@timelog/pm-shared';
import { useEffect, useState } from 'react';

import {
  FLAG_OVERRIDE_KEY,
  parseFlagsParam,
  parseStoredOverrides,
  resolveFlagFrom,
} from './featureFlags';
import type { FeatureFlag } from './featureFlags';

import { getPostHog } from '@/analytics';
import { useSession } from '@/auth';
import { DEV_TOOLS_ENABLED } from '@/config';

/**
 * Local builds only: the `?flags=` list if the URL has one (and remember it),
 * else the remembered list. `null` in a deployed build, so Staging can only
 * ever be switched by PostHog.
 */
const devOverrides = (): FeatureFlag[] | null => {
  if (!DEV_TOOLS_ENABLED) return null;
  try {
    const fromUrl = parseFlagsParam(window.location.search);
    if (fromUrl !== null) {
      window.localStorage.setItem(FLAG_OVERRIDE_KEY, JSON.stringify(fromUrl));
      return fromUrl;
    }
    return parseStoredOverrides(window.localStorage.getItem(FLAG_OVERRIDE_KEY));
  } catch {
    return null; // no localStorage (private mode) — PostHog decides, which locally means off
  }
};

/**
 * The flags forced on in this browser — always empty in a deployed build. Read
 * once per mount, like the overrides themselves (they only change on a load).
 */
export const useForcedFlags = (): FeatureFlag[] => {
  const [forced] = useState<FeatureFlag[]>(() => devOverrides() ?? []);
  return forced;
};

/** Forgets every forced flag and reloads, so the page renders as it would without them. */
export const clearForcedFlags = (): void => {
  try {
    window.localStorage.removeItem(FLAG_OVERRIDE_KEY);
  } catch {
    // Nothing stored to forget.
  }
  const url = new URL(window.location.href);
  url.searchParams.delete('flags');
  window.location.assign(url.toString());
};

// Resolved once per page load, like the budget model: remounts never re-ask.
const resolved = new Map<FeatureFlag, boolean>();

/** `pending` while PostHog is still answering; `on`/`off` once it has. */
export type FeatureFlagStatus = 'pending' | 'on' | 'off';

const toStatus = (value: boolean): FeatureFlagStatus => (value ? 'on' : 'off');

/**
 * A flag's status, for the one caller that must tell "not yet known" from
 * "off": a route that redirects when off would otherwise bounce a deep link
 * while PostHog is still answering. Off on every failure path
 * (`resolveFlagFrom`). A locally forced flag skips PostHog.
 */
export const useFeatureFlagStatus = (key: FeatureFlag): FeatureFlagStatus => {
  const session = useSession();
  const [status, setStatus] = useState<FeatureFlagStatus>(() => {
    const overrides = devOverrides();
    if (overrides !== null && overrides.length > 0) return toStatus(overrides.includes(key));
    const known = resolved.get(key);
    if (known !== undefined) return toStatus(known);
    // No client (locally, `.env` has no key) is a final answer, not a wait.
    return getPostHog() ? 'pending' : 'off';
  });

  useEffect(() => {
    if (resolved.has(key)) return undefined;
    const overrides = devOverrides();
    if (overrides !== null && overrides.length > 0) return undefined;
    const client = getPostHog();
    if (!client) {
      resolved.set(key, false);
      return undefined;
    }
    // Identity first, then flags: PostHog evaluates per person, so reading the
    // flag before identifying would answer for an anonymous visitor.
    if (!session.isAuthenticated || !session.user) return undefined;
    const subject = session.user.subject;
    client.identify(subject);
    try {
      client.group('tenant', parseSubject(subject).tenantId);
    } catch {
      // A malformed subject only costs tenant targeting, never the flag.
    }

    let cancelled = false;
    void resolveFlagFrom(client, key).then((value) => {
      resolved.set(key, value);
      if (!cancelled) setStatus(toStatus(value));
    });
    return () => {
      cancelled = true;
    };
  }, [key, session.isAuthenticated, session.user]);

  return status;
};

/** Whether a feature is on. OFF while PostHog is still answering. */
export const useFeatureFlag = (key: FeatureFlag): boolean => useFeatureFlagStatus(key) === 'on';
